// Mess-Pipeline für eine einzelne Flex-Aufnahme: PoseFrame[] -> FlexFinding[].
//
// Reuse: gait/geometry.percentile fürs robuste Min/Max (5./95. Perzentil),
// analog zum ROM-Muster in gait/rom.ts — hier bewusst OHNE Gangzyklen, da es
// sich um eine gehaltene/aktive Einzelbewegung handelt, nicht um Gehen.
// Kein Aufruf von gait/rom.ts (siehe Kommentar in movements.ts).

import type { PoseFrame, Side } from "../pose/landmarks";
import { percentile } from "../gait/geometry";
import { deriveFlexConfidence, capFlexConfidence, flexBandFromCuts } from "./bands";
import { movementsByCaptureGroup, type MovementDef } from "./movements";
import { estimateFloorReachPercent } from "./measurements";
import type { FlexFinding } from "./types";

/** Movements with a Self-vs-Self reach-% estimate layered on top of their band (§3a). */
const REACH_PERCENT_KEYS = new Set(["trunk_forward_flex"]);

const SIDE_LABEL: Record<Side, string> = { L: "links", R: "rechts" };

function meanVisibility(frames: PoseFrame[]): number {
  if (!frames.length) return 0;
  let sum = 0;
  let n = 0;
  for (const f of frames) {
    for (const l of f.landmarks) {
      sum += l.visibility;
      n++;
    }
  }
  return n ? sum / n : 0;
}

function seriesFor(
  frames: PoseFrame[],
  m: MovementDef,
  side: Side | undefined,
  aspect: number,
): number[] {
  return frames.map((f) => m.valueAt(f, side, aspect));
}

function magnitudeFor(series: number[], metric: MovementDef["metric"]): number {
  if (!series.length) return 0;
  if (metric === "excursion") {
    const lo = percentile(series, 5);
    const hi = percentile(series, 95);
    return Math.max(0, hi - lo);
  }
  return Math.max(0, percentile(series, 95));
}

/**
 * Run every movement registered for one capture group against that clip's
 * frames. Returns no Finding at all for a movement if there are too few
 * usable frames — "kein Ergebnis statt Fantasiewert" (spec §10).
 */
export function computeFlexFindings(
  captureGroup: string,
  frames: PoseFrame[],
  aspect = 1,
): FlexFinding[] {
  const MIN_FRAMES = 5;
  if (frames.length < MIN_FRAMES) return [];

  const movements = movementsByCaptureGroup(captureGroup);
  const vis = meanVisibility(frames);
  const baseConf = deriveFlexConfidence(vis, frames.length);
  const out: FlexFinding[] = [];

  for (const m of movements) {
    const sides: (Side | undefined)[] = m.perSide ? ["L", "R"] : [undefined];
    for (const side of sides) {
      const series = seriesFor(frames, m, side, aspect);
      if (!series.length) continue;
      const magnitude = magnitudeFor(series, m.metric);
      const conf = m.confidenceCap ? capFlexConfidence(baseConf, m.confidenceCap) : baseConf;

      const reachPercent = REACH_PERCENT_KEYS.has(m.key)
        ? estimateFloorReachPercent(frames, aspect) ?? undefined
        : undefined;

      out.push({
        key: side ? `${m.key}_${side}` : m.key,
        label: side ? `${m.label} (${SIDE_LABEL[side]})` : m.label,
        region: m.region,
        band: flexBandFromCuts(magnitude, m.cuts),
        magnitude,
        unit: m.unit,
        confidence: conf,
        side,
        reachPercent,
      });
    }
  }
  return out;
}
